import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Constant } from 'src/app/constant/constant';
import { LoginService } from 'src/app/services/login.service';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {

    constructor(
        private auth: LoginService,
        private _router: Router
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check the authenticated status
     *
     * @param redirectURL
     * @private
     */
    private _check(redirectURL): Observable<boolean> {
        return this.auth.check().pipe(
            switchMap((authenticated) => {
                if (!authenticated) {
                    this._router.navigate(['sign-in'], { queryParams: { redirectURL } });
                    return of(false);
                }
                return of(true);
            })
        );
    }

    getAuthenticatedToken() {
        return this.auth.getStorageToken(Constant.X_XSRF_TOKEN);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Can activate
     *
     * @param route
     * @param state
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        const url = state.url || '';
        if (url.includes('/unauthorized') || url.includes('/sign-out')) {
            return true;
        }
        if (localStorage.getItem('concurrent_evicted') === 'true') {
            this._router.navigate(['/unauthorized'], { queryParams: { reason: 'concurrent' } });
            return false;
        }

        let redirectUrl = state.url;

        if (redirectUrl === '/sign-out') {
            redirectUrl = '/';
        }

        return this._check(redirectUrl);
    }

    /**
     * Can activate child
     *
     * @param childRoute
     * @param state
     */
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const url = state.url || '';
        if (url.includes('/unauthorized') || url.includes('/sign-out')) {
            return true;
        }
        if (localStorage.getItem('concurrent_evicted') === 'true') {
            this._router.navigate(['/unauthorized'], { queryParams: { reason: 'concurrent' } });
            return false;
        }

        let redirectUrl = state.url;

        if (redirectUrl === '/sign-out') {
            redirectUrl = '/';
        }

        return this._check(redirectUrl);
    }

    /**
     * Can load
     *
     * @param route
     * @param segments
     */
    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
        const path = route.path || '';
        if (path.includes('unauthorized') || path.includes('sign-out')) {
            return true;
        }
        if (localStorage.getItem('concurrent_evicted') === 'true') {
            this._router.navigate(['/unauthorized'], { queryParams: { reason: 'concurrent' } });
            return false;
        }
        return this._check('/');
    }
}
